import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { merge, Observable, map, reduce, of, switchMap } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LocationRouteCffService } from '@fcom/core';
import { LocationType } from '@fcom/core-api';
import { BoundType, LocationInfoMap } from '@fcom/dapi';
import { FinnairBoundItem } from '@fcom/dapi/api/models/finnair-bound-item';
import { FinnairLocation } from '@fcom/dapi/api/models/finnair-location';
import { FinnairLocationType } from '@fcom/dapi/api/models/finnair-location-type';
import { LoaderTheme } from '@fcom/ui-components/components/loader/enums';
import { LanguageService } from '@fcom/ui-translate';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { resolveTailName, unique } from '@fcom/core/utils/utils';
import { BUS_AIRCRAFT_CODE } from '@fcom/common-booking/interfaces/utils.interface';
import { getAircraftTailForFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { isFlight } from '@fcom/common-booking/utils/flight.utils';

import { TimetableLocationsService } from '../../services/timetable-locations.service';
import { TimetableScheduleItem } from '../../interfaces/timetable.interface';

@Component({
  selector: 'fin-timetable-schedule',
  templateUrl: './timetable-schedule.component.html',
  styleUrls: ['timetable-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TimetableScheduleComponent {
  private locationRouteCffService = inject(LocationRouteCffService);
  private languageService = inject(LanguageService);
  private timetableLocations = inject(TimetableLocationsService);

  /** A list of bounds used to compile the schedule table. */
  readonly bounds = input.required<FinnairBoundItem[]>();
  readonly loading = input.required<boolean>();
  readonly originCode = input<string | undefined>(undefined);
  readonly destinationCode = input<string | undefined>(undefined);

  /** Defaults to the existing timetable-page key */
  readonly selectDateKey = input<string>('timetable.flightList.selectDate');

  public readonly BoundType = BoundType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly LoaderTheme = LoaderTheme;

  readonly items = computed<TimetableScheduleItem[]>(() =>
    this.sortDirectFirst(this.bounds()).map((bound) => ({
      flights: bound.itinerary.filter(isFlight).map((flight) => ({
        flightNumber: flight.flightNumber ?? '',
        departureTime: this.getTime(flight.departure.dateTime),
        arrivalTime: this.getTime(flight.arrival.dateTime),
        overnight: this.isOvernight(flight.departure.dateTime, flight.arrival.dateTime),
        icon: flight.aircraft?.code === BUS_AIRCRAFT_CODE ? SvgLibraryIcon.BUS : SvgLibraryIcon.AIRCRAFT_HORIZONTAL,
        tailClass: resolveTailName(getAircraftTailForFlight(flight)),
      })),
      duration: bound.duration,
      origin$: this.timetableLocations.getCityNameForLocationCode(bound.departure.locationCode),
      destination$: this.timetableLocations.getCityNameForLocationCode(bound.arrival.locationCode),
      departureTime: this.getTime(bound.departure.dateTime),
      arrivalTime: this.getTime(bound.arrival.dateTime),
      bound,
    }))
  );

  readonly selectedBound = signal<FinnairBoundItem | undefined>(undefined);
  readonly locations = toSignal(
    toObservable(this.selectedBound).pipe(
      switchMap((bound) => (bound ? this.getLocations(this.locationCodesFor(bound)) : of({} as LocationInfoMap)))
    ),
    { initialValue: {} as LocationInfoMap }
  );
  detailsModalOpen = false;

  openModal(item: TimetableScheduleItem): void {
    this.selectedBound.set(item.bound);
    this.detailsModalOpen = true;
  }

  closeModal(): void {
    this.detailsModalOpen = false;
  }

  private sortDirectFirst(bounds: FinnairBoundItem[]): FinnairBoundItem[] {
    return [...bounds].sort((a, b) => {
      const directDiff = Number(!this.isDirect(a)) - Number(!this.isDirect(b));
      if (directDiff !== 0) {
        return directDiff;
      }
      return a.departure.dateTime.localeCompare(b.departure.dateTime);
    });
  }

  private isDirect(bound: FinnairBoundItem): boolean {
    return bound.itinerary.filter(isFlight).length === 1;
  }

  private locationCodesFor(bound: FinnairBoundItem): string[] {
    return bound.itinerary
      .filter(isFlight)
      .reduce(
        (locationCodes, flight) => locationCodes.concat([flight.departure.locationCode, flight.arrival.locationCode]),
        [] as string[]
      )
      .filter(unique);
  }

  private getLocations(locationCodes: string[]): Observable<LocationInfoMap> {
    const locations: Array<Observable<FinnairLocation>> = locationCodes.map((locationCode) =>
      this.getLocation(locationCode)
    );
    return merge(...locations).pipe(
      reduce((acc, location) => {
        acc[location.cityCode] = location;
        return acc;
      }, {} as LocationInfoMap)
    );
  }

  private getLocation(locationCode: string): Observable<FinnairLocation> {
    return this.locationRouteCffService.bestGuessFor(locationCode, this.languageService.localeValue).pipe(
      map((location) => {
        /**
         * TODO: remove the casting when backend FinnairPicture is fixed
         */
        return {
          ...location,
          type: location.type === LocationType.AIRPORT ? FinnairLocationType.AIRPORT : FinnairLocationType.CITY,
          cityCode: locationCode,
          name: location.title,
        } as unknown as FinnairLocation;
      })
    );
  }

  private isOvernight(departureTime: string, arrivalTime: string): boolean {
    const departureDate: LocalDate = new LocalDate(this.getDate(departureTime));
    const arrivalDate: LocalDate = new LocalDate(this.getDate(arrivalTime));
    const nextDay: LocalDate = departureDate.plusDays(1);
    return (
      arrivalDate.localDay === nextDay.localDay &&
      arrivalDate.localMonth === nextDay.localMonth &&
      arrivalDate.localYear === nextDay.localYear
    );
  }

  private getDate(dateTime: string): string {
    return dateTime.substring(0, dateTime.indexOf('T'));
  }

  private getTime(dateTime: string): string {
    return dateTime.substr(dateTime.indexOf('T') + 1, 5);
  }
}
